import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import api, { trackActivity, factsheetBulkAPI } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePreview } from '../../contexts/PreviewContext';
import PortfolioSelector from './PortfolioSelector';
import FactSheetViewer from './FactSheetViewer';
import EditableContentPanel from './EditableContentPanel';
import BulkDownloadModal from './BulkDownloadModal';
import './styles.css';

// Plugin to ensure Chart.js canvases never fill with a white background
const transparentCanvasPlugin = {
  id: 'transparentCanvas',
  beforeDraw: (chart) => {
    chart.ctx.save();
    chart.ctx.globalCompositeOperation = 'destination-over';
    chart.ctx.fillStyle = 'rgba(0, 0, 0, 0)';
    chart.ctx.fillRect(0, 0, chart.width, chart.height);
    chart.ctx.restore();
  }
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  transparentCanvasPlugin
);

const FactSheetApp = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { previewDomain, isPreviewMode } = usePreview();

  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [factSheetData, setFactSheetData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [viewMode, setViewMode] = useState('view'); // 'view' or 'edit'

  const isDrummondUser = user?.is_drummond_user;

  // Bulk download state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobProgress, setBulkJobProgress] = useState(0);
  const [bulkJobStatus, setBulkJobStatus] = useState(null);
  const [bulkMessage, setBulkMessage] = useState(null);
  const bulkPollRef = useRef(null);

  // Refs to Chart.js instances for WYSIWYG PDF export
  const allocationChartRef = useRef(null);
  const performanceChartRef = useRef(null);
  const creditChartRef = useRef(null);
  const chartRefs = useMemo(() => ({
    allocation: allocationChartRef,
    performance: performanceChartRef,
    credit: creditChartRef,
  }), []);

  // Track app access on mount
  useEffect(() => {
    trackActivity('app_access', 'fact_sheets');
  }, []);

  // Load fact sheet data when portfolio selection changes
  useEffect(() => {
    if (!selectedPortfolio) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const params = {};
    if (selectedDomain) {
      params.brand_domain = selectedDomain;
    }
    if (isPreviewMode && previewDomain) {
      params.preview_domain = previewDomain;
    }

    api.get(`/api/factsheets/portfolio/${selectedPortfolio}`, { params })
      .then((response) => {
        if (cancelled) return;
        if (response.data.success) {
          setFactSheetData(response.data.data);
        } else {
          setError(response.data.message || 'Failed to load fact sheet');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching fact sheet:', err);
        setError(err.response?.data?.detail || 'Failed to load fact sheet data');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedPortfolio, selectedDomain, isPreviewMode, previewDomain]);

  const handleSelectPortfolio = (portfolioCode, domain = null) => {
    setSelectedPortfolio(portfolioCode);
    setSelectedDomain(domain);
    setViewMode('view'); // Reset to view mode when switching portfolios
  };

  const exportChartImages = useCallback(() => {
    const chartImages = {};
    const EXPORT_DPR = 3; // 3x resolution for print quality

    for (const [name, ref] of Object.entries(chartRefs)) {
      const chartInstance = ref.current;
      if (!chartInstance) continue;
      try {
        // Temporarily boost resolution for high-res export
        const origDPR = chartInstance.options.devicePixelRatio;
        chartInstance.options.devicePixelRatio = EXPORT_DPR;
        chartInstance.resize();
        chartInstance.update('none');
        chartImages[name] = chartInstance.toBase64Image('image/png');
        // Restore original DPR
        chartInstance.options.devicePixelRatio = origDPR;
        chartInstance.resize();
        chartInstance.update('none');
      } catch (err) {
        console.warn(`Failed to export chart "${name}":`, err);
      }
    }
    return chartImages;
  }, [chartRefs]);

  const handleDownloadPdf = async () => {
    if (!selectedPortfolio) return;

    setIsPdfLoading(true);
    try {
      const params = {};
      if (selectedDomain) {
        params.brand_domain = selectedDomain;
      }
      if (isPreviewMode && previewDomain) {
        params.preview_domain = previewDomain;
      }

      // Export Chart.js canvases as high-res PNGs for WYSIWYG PDF
      const chartImages = exportChartImages();

      const response = await api.post(
        `/api/factsheets/portfolio/${selectedPortfolio}/pdf`,
        { chart_images: chartImages },
        { responseType: 'blob', params }
      );

      const portfolioName = factSheetData?.portfolio?.name || 'Portfolio';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${portfolioName.replace(/\s+/g, '_')}_Fact_Sheet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      trackActivity('fact_sheet_download', selectedPortfolio);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to generate PDF');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handleContentSaved = (savedContent) => {
    if (!selectedPortfolio) return;

    // Optimistic update — apply saved content to preview immediately
    if (savedContent && factSheetData) {
      setFactSheetData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          ...savedContent,
        }
      }));
    }

    // Background refresh to sync any server-side changes
    const params = { _t: Date.now() };
    if (selectedDomain) {
      params.brand_domain = selectedDomain;
    }
    if (isPreviewMode && previewDomain) {
      params.preview_domain = previewDomain;
    }

    api.get(`/api/factsheets/portfolio/${selectedPortfolio}`, { params })
      .then((response) => {
        if (response.data.success) {
          setFactSheetData(response.data.data);
        }
      })
      .catch((err) => {
        console.error('Error refreshing fact sheet:', err);
      });
  };

  // ---- Bulk Download Handlers ----

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    };
  }, []);

  const pollBulkJobStatus = async (jobId) => {
    try {
      const status = await factsheetBulkAPI.jobStatus(jobId);
      setBulkJobProgress(status.progress || 0);
      setBulkJobStatus(status.status);

      if (status.status === 'completed') {
        if (bulkPollRef.current) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
        }
        await downloadBulkResult(jobId, status.generated, status.failed);
      } else if (status.status === 'failed') {
        if (bulkPollRef.current) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
        }
        setIsBulkGenerating(false);
        setBulkJobId(null);
        setBulkMessage({
          type: 'error',
          text: `Generation failed: ${status.error || 'Unknown error'}`,
        });
      }
    } catch (err) {
      console.error('Error polling bulk job status:', err);
      if (err.response?.status === 404) {
        if (bulkPollRef.current) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
        }
        setIsBulkGenerating(false);
        setBulkJobId(null);
        setBulkMessage({
          type: 'error',
          text: 'Job lost — the server may have restarted during generation. Please try again.',
        });
      }
    }
  };

  const downloadBulkResult = async (jobId, generated, failed) => {
    try {
      const response = await factsheetBulkAPI.download(jobId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `factsheets_${new Date().toISOString().split('T')[0]}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsBulkGenerating(false);
      setBulkJobId(null);
      setBulkJobProgress(0);

      if (failed > 0) {
        setBulkMessage({
          type: 'warning',
          text: `Generated ${generated} fact sheets successfully. ${failed} failed.`,
        });
      } else {
        setBulkMessage({
          type: 'success',
          text: `Successfully generated ${generated} fact sheets! Download started.`,
        });
      }

      trackActivity('bulk_factsheet_download', `${generated}_generated`);
    } catch (err) {
      console.error('Error downloading bulk result:', err);
      setIsBulkGenerating(false);
      setBulkJobId(null);
      setBulkMessage({ type: 'error', text: 'Failed to download fact sheets' });
    }
  };

  const handleBulkGenerate = async (items) => {
    setIsBulkGenerating(true);
    setBulkJobProgress(0);
    setBulkJobStatus('pending');
    setBulkMessage({ type: 'info', text: `Starting generation for ${items.length} fact sheets...` });

    try {
      const response = await factsheetBulkAPI.generate(items);
      setBulkJobId(response.job_id);
      setBulkMessage({
        type: 'info',
        text: `Generating ${items.length} fact sheets in background. Progress will update automatically.`,
      });

      bulkPollRef.current = setInterval(() => {
        pollBulkJobStatus(response.job_id);
      }, 2000);
    } catch (err) {
      console.error('Error starting bulk generation:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to start generation';
      setBulkMessage({ type: 'error', text: errorMessage });
      setIsBulkGenerating(false);
    }
  };

  const handleOpenBulkDownload = () => {
    setBulkMessage(null);
    setIsBulkModalOpen(true);
  };

  const handleCloseBulkModal = () => {
    if (!isBulkGenerating) {
      setIsBulkModalOpen(false);
      setBulkMessage(null);
    }
  };

  const isEditMode = viewMode === 'edit';

  // Determine effective theme: use branding from API response when viewing a branded portfolio
  const effectiveTheme = factSheetData?.branding || theme;

  return (
    <div className="fact-sheet-app" style={{
      '--primary-navy': (factSheetData?.branding?.primary_color) || '#072040',
      '--secondary-gold': effectiveTheme.secondary_color || '#f0a500',
    }}>
      <div className="factsheet-layout">
        {/* Sidebar */}
        <PortfolioSelector
          selectedPortfolio={selectedPortfolio}
          selectedDomain={selectedDomain}
          onSelectPortfolio={handleSelectPortfolio}
          isDrummondUser={isDrummondUser}
        />

        {/* Main Content */}
        <div className="factsheet-main">
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* No portfolio selected */}
          {!selectedPortfolio && !isLoading && (
            <div className="factsheet-empty">
              <i className="bi bi-arrow-left-circle" style={{ fontSize: '3rem', color: '#072040', opacity: 0.3 }}></i>
              <p className="mt-3 text-muted">Select a portfolio from the sidebar to view its fact sheet.</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading fact sheet data...</p>
            </div>
          )}

          {/* Fact Sheet Content */}
          {factSheetData && !isLoading && (
            <div className={`fact-sheet-wrapper ${isEditMode ? 'split-view' : ''}`}>
              <div className="fact-sheet-content">
                <FactSheetViewer
                  data={factSheetData}
                  theme={effectiveTheme}
                  chartRefs={chartRefs}
                />
              </div>

              {/* Right panel: actions + optional edit form */}
              <div className="factsheet-right-panel">
                <div className="factsheet-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleDownloadPdf}
                    disabled={isPdfLoading}
                  >
                    {isPdfLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-download me-2"></i>
                        Download PDF
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleOpenBulkDownload}
                  >
                    <i className="bi bi-collection me-2"></i>
                    Bulk Download
                  </button>
                  {isDrummondUser && (
                    <div className="mode-toggle">
                      <button
                        className={`mode-toggle-btn ${viewMode === 'view' ? 'active' : ''}`}
                        onClick={() => setViewMode('view')}
                      >
                        <i className="bi bi-eye me-1"></i>
                        View
                      </button>
                      <button
                        className={`mode-toggle-btn ${viewMode === 'edit' ? 'active' : ''}`}
                        onClick={() => setViewMode('edit')}
                      >
                        <i className="bi bi-pencil-square me-1"></i>
                        Edit
                      </button>
                    </div>
                  )}
                </div>

                {isEditMode && isDrummondUser && (
                  <div className="edit-panel-content">
                    <EditableContentPanel
                      portfolioCode={selectedPortfolio}
                      domain={selectedDomain}
                      currentContent={factSheetData.content}
                      onContentSaved={handleContentSaved}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Download Modal */}
      <BulkDownloadModal
        isOpen={isBulkModalOpen}
        onClose={handleCloseBulkModal}
        isDrummondUser={isDrummondUser}
        isGenerating={isBulkGenerating}
        jobProgress={bulkJobProgress}
        jobStatus={bulkJobStatus}
        message={bulkMessage}
        onGenerate={handleBulkGenerate}
      />
    </div>
  );
};

export default FactSheetApp;
